import type { FC } from 'react';
import { useCallback } from 'react';
import type { CountdownRendererFn } from 'react-countdown';
import { toZonedTime } from 'date-fns-tz';
import { useCurrentTime, useTimerTranslations, zeroPad } from '@mwl/core-lib';
import { useMediaWidthDown } from '@/hooks';
import { Countdown } from '../Countdown/Countdown';
import { Counter } from '../Counter/Counter';
import { CenteredColon } from './components/CenteredColon/CenteredColon';
import type { CardTimerProps } from './CardTimer.types';
import styles from './CardTimer.module.scss';
const CardTimer: FC<CardTimerProps> = ({
  className,
  hideSeconds = false,
  counterClasses,
  counterClassName,
  useTimezone = true,
  variant = 'light',
  skipMobileBreakpoint = false,
  ...props
}) => {
  const timerTranslations = useTimerTranslations();
  const {
    currentTimeZone
  } = useCurrentTime();
  const isMobile = useMediaWidthDown('screenXs');
  const getNow = () => useTimezone ? toZonedTime(Date.now(), currentTimeZone).getTime() : new Date().getTime();
  const rendererCountdown: CountdownRendererFn = useCallback(({
    days,
    hours,
    minutes,
    seconds,
    completed
  }) => {
    return <div className={cn(styles.root, className)}>
          {(!!days || completed) && !Number.isNaN(days) && <>
              {isMobile && !skipMobileBreakpoint ? <div className={styles.mobileItem}>
                  {zeroPad(days, 2)}
                  {timerTranslations.d}
                  <CenteredColon />
                </div> : <Counter classes={counterClasses} className={cn(styles.counter, counterClassName)} variant={variant} topContent={days} skipMobileBreakpoint={skipMobileBreakpoint} bottomContent={timerTranslations.day} />}
            </>}
          {(!!days || !!hours || completed) && !Number.isNaN(hours) && <>
              {isMobile && !skipMobileBreakpoint ? <div className={styles.mobileItem}>
                  {zeroPad(hours, 2)}
                  {timerTranslations.h}
                  <CenteredColon />
                </div> : <Counter classes={counterClasses} className={cn(styles.counter, counterClassName)} variant={variant} skipMobileBreakpoint={skipMobileBreakpoint} topContent={zeroPad(hours, 2)} bottomContent={timerTranslations.hrs} />}
            </>}
          {!Number.isNaN(minutes) && <>
              {isMobile && !skipMobileBreakpoint ? <div className={styles.mobileItem}>
                  {zeroPad(minutes, 2)}
                  {timerTranslations.m}
                  <CenteredColon />
                </div> : <Counter classes={counterClasses} className={cn(styles.counter, counterClassName)} variant={variant} skipMobileBreakpoint={skipMobileBreakpoint} topContent={zeroPad(minutes, 2)} bottomContent={timerTranslations.min} />}
            </>}
          {!Number.isNaN(seconds) && !hideSeconds && <>
              {isMobile && !skipMobileBreakpoint ? <div className={styles.mobileItem}>
                  {zeroPad(seconds, 2)}
                  {timerTranslations.s}
                </div> : <Counter classes={counterClasses} className={cn(styles.counter, counterClassName)} variant={variant} skipMobileBreakpoint={skipMobileBreakpoint} topContent={zeroPad(seconds, 2)} bottomContent={timerTranslations.sec} />}
            </>}
        </div>;
  }, [className, timerTranslations, isMobile, variant, counterClasses, counterClassName, hideSeconds, skipMobileBreakpoint]);
  return <Countdown {...props} now={getNow} renderer={rendererCountdown} data-sentry-element="Countdown" data-sentry-component="CardTimer" data-sentry-source-file="CardTimer.tsx" />;
};
export * from './CardTimer.types';
export { CardTimer };